/**
 * V1 — "Kinetic" (Final)
 * 12 sections. Every layout distinct. No two sections use the same visual pattern.
 * Dramatic scale jumps. Full-bleed moments. Pull-quote typography.
 * Full-viewport showstopper headlines. Edge-to-edge screenshots.
 * Fonts: Unbounded (display) + Outfit (body)
 */
import { useRef, useLayoutEffect, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  ArrowRight, FolderOpen, Globe, UserCheck, Users, Sparkles,
  Film, Radio, BarChart3, Clock, MessageSquare, Star,
  CheckCircle2, Headphones,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const D = "'Unbounded', cursive";
const B = "'Outfit', sans-serif";
const TEAL = '#2EC4B6';
const CORAL = '#ef5228';

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

export function LandingV1() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const h = (e: MouseEvent) => setMousePos({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight });
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      /* ── Hero entrance ── */
      gsap.fromTo('.v1-h0', { x: -120, opacity: 0 }, { x: 0, opacity: 1, duration: 1, ease: 'power4.out', delay: 0.1 });
      gsap.fromTo('.v1-h1', { x: 120, opacity: 0 }, { x: 0, opacity: 1, duration: 1, ease: 'power4.out', delay: 0.25 });
      gsap.fromTo('.v1-h2', { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power4.out', delay: 0.4 });
      gsap.fromTo('.v1-hsub', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, delay: 0.6 });
      gsap.fromTo('.v1-hcta', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, delay: 0.8 });

      /* diagonal strip widens */
      gsap.to('.v1-strip', {
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', ease: 'none',
        scrollTrigger: { trigger: '.v1-hero', start: 'top top', end: '+=55%', scrub: 1 },
      });

      /* hero word parallax */
      gsap.to('.v1-h0', { y: -140, scrollTrigger: { trigger: '.v1-hero', start: 'top top', end: 'bottom top', scrub: true } });
      gsap.to('.v1-h1', { y: -70, scrollTrigger: { trigger: '.v1-hero', start: 'top top', end: 'bottom top', scrub: true } });
      gsap.to('.v1-h2', { y: -190, scrollTrigger: { trigger: '.v1-hero', start: 'top top', end: 'bottom top', scrub: true } });

      /* ── Screenshot fade-in ── */
      gsap.fromTo('.v1-screen', { y: 40, opacity: 0, scale: 0.97 }, {
        y: 0, opacity: 1, scale: 1, duration: 0.9, ease: 'power2.out',
        scrollTrigger: { trigger: '.v1-screen', start: 'top 85%' },
      });

      /* ── Pain points: each slides up individually ── */
      document.querySelectorAll('.v1-pain').forEach((el) => {
        gsap.fromTo(el, { y: 24, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.5, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 88%' },
        });
      });

      /* ── Generic reveals ── */
      document.querySelectorAll('.v1-reveal').forEach((el) => {
        gsap.fromTo(el, { y: 36, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 82%' },
        });
      });

      /* ── Parallax photo backgrounds ── */
      document.querySelectorAll('.v1-parallax').forEach((el) => {
        gsap.to(el, {
          y: '-12%', ease: 'none',
          scrollTrigger: { trigger: el.parentElement!, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });

      /* ── Collaboration showstopper: scale landing ── */
      gsap.fromTo('.v1-showstopper', { scale: 0.92, opacity: 0 }, {
        scale: 1, opacity: 1, duration: 0.8, ease: 'power2.out',
        scrollTrigger: { trigger: '.v1-showstopper', start: 'top 70%' },
      });

      /* ── Stats count up ── */
      document.querySelectorAll('.v1-count').forEach((el) => {
        const target = parseInt(el.getAttribute('data-val') || '0');
        gsap.fromTo(el, { innerText: '0' }, {
          innerText: target, duration: 2, ease: 'power2.out', snap: { innerText: 1 },
          scrollTrigger: { trigger: el, start: 'top 85%' },
        });
      });

      /* ── Number pops (every artist section) ── */
      document.querySelectorAll('.v1-numpop').forEach((el, i) => {
        gsap.fromTo(el, { scale: 0.6, opacity: 0 }, {
          scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2)',
          delay: i * 0.15,
          scrollTrigger: { trigger: '.v1-numbers', start: 'top 70%' },
        });
      });

      /* CTA glow */
      gsap.to('.v1-glow', { opacity: 0.6, duration: 2, repeat: -1, yoyo: true, ease: 'sine.inOut' });

    }, containerRef.current!);
    return () => ctx.revert();
  }, []);

  /* ━━━ RENDER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
  return (
    <div ref={containerRef} style={{ fontFamily: B, background: '#050505', color: '#bbb', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* Cursor gradient */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        background: `radial-gradient(600px circle at ${mousePos.x * 100}% ${mousePos.y * 100}%, ${TEAL}08, transparent 60%)`,
        transition: 'background 0.3s ease',
      }} />

      {/* Noise */}
      <div className="fixed inset-0 pointer-events-none z-[9998] opacity-[0.035]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* ════════ NAV ════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 mix-blend-difference">
        <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <img src="/eventtools-logo.svg" alt="" className="w-9 h-9" />
            <span style={{ fontFamily: D, fontWeight: 600, fontSize: 15, color: '#fff', letterSpacing: '0.04em' }}>EVENT.TOOLS</span>
          </Link>
          <Link to="/signin" className="text-[13px] font-medium tracking-wider uppercase" style={{ color: '#fff' }}>
            Get access →
          </Link>
        </div>
      </nav>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 1. HERO — Full-screen, diagonal clip, massive text               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="v1-hero relative h-screen flex items-center overflow-hidden">
        <div className="v1-strip absolute inset-0 z-0" style={{ clipPath: 'polygon(55% 0%, 75% 0%, 45% 100%, 25% 100%)' }}>
          <img src="/images/hero_crowd.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.5) saturate(1.2)' }} />
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${TEAL}30, ${CORAL}20)`, mixBlendMode: 'overlay' }} />
        </div>

        <div className="relative z-10 w-full px-8 max-w-[1400px] mx-auto">
          <div style={{ fontFamily: D }}>
            <div className="v1-h0" style={{ fontSize: 'clamp(44px, 9vw, 130px)', fontWeight: 900, lineHeight: 0.92, color: '#fff', letterSpacing: '-0.04em' }}>THAT FLYER</div>
            <div className="v1-h1 md:ml-[12vw]" style={{ fontSize: 'clamp(44px, 9vw, 130px)', fontWeight: 900, lineHeight: 0.92, letterSpacing: '-0.04em', WebkitTextStroke: '2px #fff', color: 'transparent' }}>ISN'T GOING</div>
            <div className="v1-h2" style={{ fontSize: 'clamp(32px, 6vw, 85px)', fontWeight: 300, lineHeight: 1, color: TEAL, letterSpacing: '-0.02em', marginTop: '0.12em' }}>to make itself.</div>
          </div>

          {/* Subline — prominent, not a footnote */}
          <div className="v1-hsub mt-7">
            <p style={{ fontSize: 'clamp(18px, 2.2vw, 26px)', fontWeight: 500, color: '#e0e0e0', lineHeight: 1.3, letterSpacing: '-0.01em' }}>
              (But with Event.Tools, it basically does.)
            </p>
            <p className="mt-4 max-w-lg" style={{ fontSize: 16, lineHeight: 1.75, color: '#999' }}>
              Every day your event goes unannounced is a day of lost reach, lost ticket sales, lost momentum.
              We built the tool that turns "I'll get to it tomorrow" into "it's already done."
            </p>
          </div>

          <div className="v1-hcta mt-8 flex items-center gap-5">
            <Link to="/signin" className="relative inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-md overflow-hidden" style={{ background: CORAL, color: '#fff' }}>
              <div className="v1-glow absolute inset-0 opacity-0" style={{ background: `radial-gradient(circle, ${CORAL}80, transparent)`, filter: 'blur(15px)' }} />
              <span className="relative z-10 flex items-center gap-2">Get early access <ArrowRight size={16} /></span>
            </Link>
            <a href="#product" className="text-sm tracking-wider uppercase" style={{ color: '#555' }}>See the product ↓</a>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 2. ASSET VAULT — Full-width screenshot reveal                    */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section id="product" className="py-24 px-8">
        <div className="max-w-[1300px] mx-auto">
          <div className="v1-reveal mb-4">
            <div className="flex items-center gap-3 mb-4">
              <FolderOpen size={18} style={{ color: TEAL }} />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Asset Vault</span>
            </div>
            <h2 style={{ fontFamily: D, fontSize: 'clamp(32px, 4.5vw, 60px)', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.04em' }}>
              Upload once. Never dig again.
            </h2>
            <p className="mt-4 max-w-xl" style={{ fontSize: 16, lineHeight: 1.7, color: '#888' }}>
              Every logo, headshot, and brand element in one organized vault.
              Tagged. Searchable. Transparency-checked and ready to use.
              Stop treating your hard drive like a junk drawer.
            </p>
          </div>
          {/* Screenshot — edge-to-edge, dominant */}
          <div className="v1-screen mt-10 rounded-2xl overflow-hidden" style={{
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: `0 50px 120px -30px ${TEAL}12, 0 0 0 1px rgba(255,255,255,0.03)`,
          }}>
            <img src="/screenshot-library.png" alt="Event.Tools Asset Library" className="w-full block" />
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 3. PAIN POINTS — Stacked large pull-quotes                       */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-8">
        <div className="max-w-[800px] mx-auto">
          <div className="v1-reveal mb-14">
            <h2 style={{ fontFamily: D, fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.04em' }}>
              Sound familiar?
            </h2>
          </div>

          <div className="space-y-0">
            {[
              { color: TEAL, text: "Digging through 4 months of texts from a number you never saved looking for a logo someone definitely sent you. Definitely." },
              { color: CORAL, text: "Manually placing the same 6 DJ logos onto a new flyer. For the 23rd week in a row. By hand." },
              { color: TEAL, text: "\u201CHere\u2019s my logo.\u201D It\u2019s a JPEG. On a white background. At 72 DPI." },
              { color: CORAL, text: "12 artists on the lineup. 2 made it onto the flyer. The VJ? Not even a mention." },
              { color: TEAL, text: "Someone hands you a USB stick with their logo 3 minutes before their set. You don\u2019t even know which slot they\u2019re in because nobody sent the set times." },
              { color: CORAL, text: "The artist rebranded 6 months ago. Your flyer still has the 2023 logo. Nobody told you." },
            ].map((p, i) => (
              <div key={i} className="v1-pain py-7" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="flex gap-5 items-start">
                  <div className="mt-2.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
                  <p style={{ fontSize: 'clamp(17px, 1.8vw, 21px)', fontWeight: 500, lineHeight: 1.6, color: '#ddd', letterSpacing: '-0.01em' }}>
                    {p.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <p className="v1-reveal mt-10 text-center" style={{ fontSize: 15, color: '#666' }}>
            If even one of these made you exhale through your nose — <span style={{ color: TEAL }}>this is for you.</span>
          </p>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 4. SCENE DATABASE — Huge centered statement, max negative space   */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-28 px-8">
        <div className="max-w-[900px] mx-auto text-center v1-reveal">
          <div className="w-12 h-px mx-auto mb-8" style={{ background: TEAL }} />
          <Globe size={22} style={{ color: TEAL }} className="mx-auto mb-5" />
          <h2 style={{ fontFamily: D, fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em' }}>
            Your local scene, indexed.
          </h2>
          <p className="mt-6 max-w-lg mx-auto" style={{ fontSize: 17, lineHeight: 1.75, color: '#888' }}>
            Browse a community-maintained library of DJ logos and artist assets in your city.
            If they play in your scene, their branding is already here.
            No more "hey can you send me your logo" messages that never get a reply.
          </p>
          <div className="w-12 h-px mx-auto mt-8" style={{ background: TEAL }} />
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 5. ARTIST PROFILES — Full-bleed photo, massive overlaid text     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[75vh] flex items-center overflow-hidden">
        <div className="v1-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/planning_dj_headphones.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.22) saturate(1.3)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050505, transparent 20%, transparent 80%, #050505)' }} />

        <div className="relative z-10 w-full px-8 py-20">
          <div className="max-w-[1100px] mx-auto">
            <div className="v1-reveal">
              <div className="flex items-center gap-3 mb-5">
                <UserCheck size={18} style={{ color: CORAL }} />
                <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: CORAL }}>Artist Profiles</span>
              </div>
              <h2 style={{ fontFamily: D, fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.04em' }}>
                Let artists manage their<br />own branding.
              </h2>
              <p className="mt-5 max-w-lg" style={{ fontSize: 16, lineHeight: 1.75, color: '#aaa' }}>
                Artist-managed profiles mean the logo on your next flyer is always the current one.
                When they rebrand, it updates everywhere — you don't have to chase anyone down.
                Revolutionary concept: let people manage their own stuff.
              </p>
            </div>
            {/* Screenshot floating with overlap + shadow */}
            <div className="v1-screen mt-12 rounded-2xl overflow-hidden relative" style={{
              maxWidth: 900,
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: `0 50px 120px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.03)`,
            }}>
              <img src="/screenshot-events.png" alt="Artist Profiles" className="w-full block" />
            </div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 6. EVERY ARTIST — Numbers-driven layout                          */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="v1-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/distribute_dj_lasers.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.25) saturate(1.4)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050505, transparent 15%, transparent 85%, #050505)' }} />

        <div className="relative z-10 w-full px-8 py-20">
          <div className="max-w-[1100px] mx-auto">
            {/* Giant numbers */}
            <div className="v1-numbers flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 mb-12">
              {[
                { num: '12', word: 'artists', color: TEAL },
                { num: '12', word: 'flyers', color: '#fff' },
                { num: '1', word: 'click', color: CORAL },
              ].map((n, i) => (
                <div key={i} className="v1-numpop text-center">
                  <div style={{ fontFamily: D, fontSize: 'clamp(56px, 10vw, 100px)', fontWeight: 900, color: n.color, lineHeight: 0.85, letterSpacing: '-0.05em' }}>
                    {n.num}
                  </div>
                  <div className="mt-1 text-sm tracking-widest uppercase" style={{ color: '#888' }}>{n.word}</div>
                </div>
              ))}
            </div>

            <div className="max-w-2xl mx-auto v1-reveal">
              <div className="backdrop-blur-xl p-8 rounded-2xl" style={{ background: 'rgba(8,8,8,0.6)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-3 mb-4">
                  <Star size={18} style={{ color: CORAL }} />
                  <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: CORAL }}>Everyone plays. Everyone's on it.</span>
                </div>
                <p style={{ fontSize: 16, lineHeight: 1.75, color: '#bbb' }}>
                  Make it dead simple to include every artist on the lineup — the headliner, the opener, the b2b, the VJ.
                  If they're part of the night, they're on the flyer. No one left off. No one forgotten.
                </p>
                <div className="h-px my-5" style={{ background: 'rgba(255,255,255,0.06)' }} />
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles size={18} style={{ color: TEAL }} />
                  <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Individualized flyers</span>
                </div>
                <p style={{ fontSize: 16, lineHeight: 1.75, color: '#bbb' }}>
                  Auto-generate a personalized promo flyer for every artist on the lineup.
                  When a DJ gets their own featured version, they actually share it.
                  When they share it, their fans show up. When their fans show up, everybody wins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 7. RECLAIM YOUR TIME — Full-bleed photo, emotional gut-punch     */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[65vh] flex items-center overflow-hidden">
        <div className="v1-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/planning_dj_mixing.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.22) saturate(1.2)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050505, transparent 15%, transparent 85%, #050505)' }} />

        <div className="relative z-10 w-full px-8 py-20">
          <div className="max-w-[1000px] mx-auto">
            <div className="v1-reveal">
              <div className="flex items-center gap-3 mb-5">
                <Headphones size={18} style={{ color: TEAL }} />
                <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Reclaim your time</span>
              </div>
              <h2 style={{ fontFamily: D, fontSize: 'clamp(30px, 4.5vw, 52px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em' }}>
                You signed up to DJ.<br />
                <span style={{ color: CORAL }}>Not to be a marketing department.</span>
              </h2>
              <p className="mt-5 max-w-xl" style={{ fontSize: 16, lineHeight: 1.75, color: '#aaa' }}>
                The industry quietly turned every artist into a full-time content creator who occasionally plays music.
                Asset hunting, flyer design, promo coordination — none of this is why you started.
                Event.Tools automates the marketing busywork so you can get back to the part you actually care about.
              </p>
              <div className="mt-8 space-y-3">
                {[
                  'Auto-generated promo you didn\'t have to design.',
                  'Asset library you didn\'t have to organize twice.',
                  'Social content you didn\'t have to beg anyone for.',
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 size={16} style={{ color: TEAL, flexShrink: 0 }} />
                    <span style={{ fontSize: 15, color: '#ccc' }}>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 8. MOTION & SOCIAL — Short, punchy, dramatic scale               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-28 px-8">
        <div className="max-w-[900px] mx-auto text-center v1-reveal">
          <Film size={22} style={{ color: CORAL }} className="mx-auto mb-5" />
          <h2 style={{ fontFamily: D, fontSize: 'clamp(34px, 5vw, 60px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em' }}>
            Static flyers are for<br />telephone poles.
          </h2>
          <p className="mt-5 max-w-md mx-auto" style={{ fontSize: 16, lineHeight: 1.75, color: '#888' }}>
            Add motion to your flyers for social media. Animated text, moving backgrounds, logo reveals — the content that actually stops the scroll.
            A moving flyer gets 3x the engagement. Your event deserves to move.
          </p>
          {/* Shimmer bar */}
          <div className="mt-8 mx-auto w-56 h-1 rounded-full overflow-hidden" style={{ background: '#111' }}>
            <div className="h-full rounded-full" style={{
              background: `linear-gradient(90deg, ${CORAL}, ${TEAL})`,
              animation: 'v1shimmer 2.5s ease-in-out infinite',
              width: '50%',
            }} />
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 9. LIVE RESOURCES — Compact icon-led blocks                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-8 relative" style={{ background: '#080808' }}>
        {/* Subtle background image at low opacity */}
        <div className="absolute inset-0 opacity-[0.06]">
          <img src="/images/tools_dj_spin.jpg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-[900px] mx-auto">
          <div className="v1-reveal">
            <div className="flex items-center gap-3 mb-4">
              <Radio size={18} style={{ color: TEAL }} />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Live Event Resources</span>
            </div>
            <h2 style={{ fontFamily: D, fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 800, color: '#fff', lineHeight: 1.05, letterSpacing: '-0.04em' }}>
              No more USB sticks at soundcheck.
            </h2>
            <p className="mt-4 mb-10 max-w-lg" style={{ fontSize: 16, lineHeight: 1.75, color: '#888' }}>
              An auto-updated event resource page your VJ and AV crew can actually rely on.
              Shared days before the event. Not minutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
            {[
              { icon: CheckCircle2, text: 'Every logo with proper transparency — no more white-background JPEGs.' },
              { icon: Clock, text: 'Correct set times published ahead of time, not scribbled on a napkin.' },
              { icon: MessageSquare, text: 'Special instructions before soundcheck — not shouted during.' },
              { icon: FolderOpen, text: 'No more scouring email wondering if an asset ever arrived.' },
            ].map((item, i) => (
              <div key={i} className="v1-reveal flex items-start gap-3">
                <item.icon size={16} style={{ color: TEAL, marginTop: 4, flexShrink: 0 }} />
                <p style={{ fontSize: 15, lineHeight: 1.65, color: '#bbb' }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 10. COLLABORATION — Full-viewport showstopper headline           */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="v1-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/teams_dj_smoke.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.15) saturate(1.2)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050505, transparent 20%, transparent 80%, #050505)' }} />

        <div className="relative z-10 text-center px-8 py-20">
          {/* THE showstopper — massive, alone, impossible to miss */}
          <h2 className="v1-showstopper" style={{
            fontFamily: D,
            fontSize: 'clamp(36px, 7vw, 80px)',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1,
            letterSpacing: '-0.04em',
            maxWidth: '14ch',
            margin: '0 auto',
          }}>
            Stop being the only one who cares.
          </h2>

          <div className="mt-16 max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Users, t: 'Team roles', d: 'Give everyone a role and visibility. No more lone-wolf event planning.', c: TEAL },
              { icon: MessageSquare, t: 'Feedback loops', d: 'Collect ideas without them dying in read receipts. People show up differently when they feel heard.', c: CORAL },
              { icon: Sparkles, t: 'Unified effort', d: 'Everyone works as a crew — not misdirected individuals wondering what the plan is.', c: TEAL },
            ].map((card, i) => (
              <div key={i} className="v1-reveal text-left">
                <card.icon size={20} style={{ color: card.c }} className="mb-3" />
                <h3 className="text-[15px] font-bold mb-2" style={{ fontFamily: D, color: '#fff', letterSpacing: '-0.01em' }}>{card.t}</h3>
                <p className="text-[13px] leading-relaxed" style={{ color: '#888' }}>{card.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 11. EVENT HISTORY + IMPACT — Giant stat counters                  */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-28 px-8">
        <div className="max-w-[1100px] mx-auto">
          {/* Stats — massive, dominant */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {[
              { val: 30, suffix: 'min', label: 'Saved per flyer' },
              { val: 100, suffix: '%', label: 'Artists represented' },
              { val: 3, suffix: 'x', label: 'More shares' },
              { val: 1, suffix: '', label: 'Unified team' },
            ].map((s, i) => (
              <div key={i} className="text-center v1-reveal">
                <div style={{ fontFamily: D, fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#fff', letterSpacing: '-0.04em' }}>
                  <span className="v1-count" data-val={s.val}>0</span>{s.suffix}
                </div>
                <div className="text-[11px] tracking-widest uppercase mt-2" style={{ color: '#555' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Two-column: history + impact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="v1-reveal">
              <div className="flex items-center gap-3 mb-4">
                <Clock size={18} style={{ color: TEAL }} />
                <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: TEAL }}>Event History</span>
              </div>
              <h3 style={{ fontFamily: D, fontSize: 'clamp(24px, 2.5vw, 36px)', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.03em' }}>
                Your legacy. Mapped.
              </h3>
              <p className="mt-4" style={{ fontSize: 16, lineHeight: 1.75, color: '#888' }}>
                Upload your entire archive of past flyers and let AI reconstruct your event timeline.
                Every lineup, every venue, every date — organized and searchable.
                Your whole history, finally in one place.
              </p>
            </div>
            <div className="v1-reveal">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 size={18} style={{ color: CORAL }} />
                <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: CORAL }}>Impact Tracking</span>
              </div>
              <h3 style={{ fontFamily: D, fontSize: 'clamp(24px, 2.5vw, 36px)', fontWeight: 800, color: '#fff', lineHeight: 1.1, letterSpacing: '-0.03em' }}>
                See who moved the needle.
              </h3>
              <p className="mt-4" style={{ fontSize: 16, lineHeight: 1.75, color: '#888' }}>
                Track which artists drive turnout. See who's sharing, who's engaging, who's actually pulling people through the door.
                Make booking decisions with data, not just vibes.
              </p>
              <p className="mt-2" style={{ fontSize: 15, color: '#666', fontStyle: 'italic' }}>
                (Okay, vibes too. But also data.)
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 12. CTA — Atmospheric close                                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="v1-parallax absolute inset-0 scale-[1.15]">
          <img src="/images/create_dj_backlit.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.22) saturate(1.3)' }} />
        </div>
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, ${TEAL}08, transparent 60%)` }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #050505, transparent 20%, transparent 80%, #050505)' }} />

        <div className="relative z-10 text-center px-8 py-20 v1-reveal">
          <h2 style={{ fontFamily: D, fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 900, color: '#fff', lineHeight: 0.95, letterSpacing: '-0.04em' }}>
            Your next event<br />
            <span style={{ color: TEAL }}>starts here.</span>
          </h2>
          <p className="mt-5 mb-10 max-w-md mx-auto" style={{ color: '#888', fontSize: 16 }}>
            Join early access. Stop hunting for assets. Stop making flyers alone.
            Start making every event look like a headline act.
          </p>
          <Link to="/signin" className="relative inline-flex items-center gap-2 px-8 py-4 text-sm font-semibold rounded-md overflow-hidden" style={{ background: CORAL, color: '#fff' }}>
            <div className="v1-glow absolute inset-0 opacity-0" style={{ background: `radial-gradient(circle, ${CORAL}80, transparent)`, filter: 'blur(15px)' }} />
            <span className="relative z-10 flex items-center gap-2">Get early access <ArrowRight size={16} /></span>
          </Link>
        </div>
      </section>


      {/* ════════ FOOTER ════════ */}
      <footer className="py-10 px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-[1400px] mx-auto flex items-center justify-between flex-wrap gap-4">
          <span style={{ fontFamily: D, fontWeight: 600, fontSize: 12, color: '#444', letterSpacing: '0.05em' }}>EVENT.TOOLS</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-[11px] hover:text-white transition-colors" style={{ color: '#666' }}>Privacy Policy</Link>
            <Link to="/terms" className="text-[11px] hover:text-white transition-colors" style={{ color: '#666' }}>Terms of Service</Link>
            <span className="text-[11px]" style={{ color: '#333' }}>© {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes v1shimmer {
          0%, 100% { transform: translateX(-30%); opacity: 0.4; }
          50% { transform: translateX(160%); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
